import { useEffect, useId, useMemo, useState } from "react";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Match } from "effect";
import { useSearchParams } from "react-router";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import {
  DIETARY_BADGES,
  ITEM_AVAILABILITY_CHANNEL,
  ItemAvailabilityEventSchema,
  OkItemAvailability,
  OkItemAvailabilityChanged,
} from "../../../packages/common/src/schemas/item-availability.ts";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { useLanguage } from "#i18n.tsx";
import {
  byCategory,
  chooseMarket,
  lastMarket,
  rememberMarket,
  withAvailability,
  type AvailabilityChange,
} from "#item-availability.ts";
import { locationsQueryOptions } from "#locations.ts";
import { OrderEvents } from "#order-events.ts";

const menuKey = (locationId: string | undefined) =>
  ["item-availability", locationId] as const;

/** Apply one switch to that market's loaded list, if it is loaded. */
const applyChange = (
  queryClient: QueryClient,
  locationId: string,
  change: AvailabilityChange,
) =>
  queryClient.setQueryData<OkItemAvailability>(menuKey(locationId), (current) =>
    current
      ? new OkItemAvailability({
          locationId,
          items: withAvailability(current.items, change),
        })
      : current,
  );

/**
 * Switch Square items on and off at one market, for its kiosks and online
 * orders. Square stock counts are ignored, so an item stays on sale until
 * someone switches it off.
 */
export function ItemAvailabilityPage() {
  const { api, state } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  const marketField = useId();
  const locations = useQuery(
    locationsQueryOptions(
      api,
      state.kind === "authenticated" ? state.userId : undefined,
    ),
  );
  const markets = locations.data?.locations ?? [];
  const market = chooseMarket(markets, searchParams.get("market"), lastMarket());
  const queryKey = menuKey(market);
  const query = useQuery<OkItemAvailability, ApiError>({
    queryKey,
    enabled: market !== undefined,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({
          path: `/item-availability/${encodeURIComponent(market ?? "")}`,
          schema: OkItemAvailability,
          signal,
        }),
        signal,
      ),
  });

  const chooseShownMarket = (id: string) => {
    rememberMarket(id);
    setSearchParams(
      (params) => {
        params.set("market", id);
        return params;
      },
      { replace: true },
    );
  };

  // Other Landpad screens and the kiosks switch items too.
  useEffect(() => {
    const reload = () =>
      void queryClient.invalidateQueries({ queryKey: ["item-availability"] });
    const events = new OrderEvents({
      config: api.config,
      credential: () => api.credential(),
      channel: ITEM_AVAILABILITY_CHANNEL,
      state: () => undefined,
      resynchronise: reload,
      event: (value) => {
        const parsed = decodeUnknownOption(ItemAvailabilityEventSchema, value);
        if (!parsed) return reload();
        applyChange(queryClient, parsed.payload.locationId, parsed.payload);
      },
    });
    events.start();

    return () => events.stop();
  }, [api, queryClient]);

  const mutation = useMutation({
    retry: false,
    mutationFn: (change: AvailabilityChange & { readonly locationId: string }) =>
      runEffectMutation(
        api.requestEffect({
          path: `/item-availability/${encodeURIComponent(change.locationId)}/${encodeURIComponent(change.itemId)}`,
          method: "PUT",
          body: { available: change.available },
          schema: OkItemAvailabilityChanged,
        }),
      ),
    onMutate: (change) => {
      setNotice(undefined);
      applyChange(queryClient, change.locationId, change);
    },
    onError: (_error, change) => {
      applyChange(queryClient, change.locationId, {
        ...change,
        available: !change.available,
      });
    },
    onSuccess: (result) => {
      if (result.liveUpdate === "delayed") setNotice(t("availability.delayed"));
    },
  });

  const items = query.data?.items;
  const groups = useMemo(() => byCategory(items ?? []), [items]);
  const switchedOff = (items ?? []).filter((item) => !item.available);

  return (
    <div className="availability-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{t("availability.eyebrow")}</p>
          <h1>{t("availability.title")}</h1>
          <p className="muted">{t("availability.description")}</p>
        </div>
        {markets.length > 1 && market && (
          <div className="availability-market">
            <label htmlFor={marketField}>{t("availability.market")}</label>
            <select
              id={marketField}
              value={market}
              onChange={(event) => chooseShownMarket(event.target.value)}
            >
              {markets.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </header>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {mutation.error && (
        <p className="alert" role="alert">
          {t("availability.failed")}
        </p>
      )}
      {locations.isError && (
        <div className="alert" role="alert">
          <p>{locations.error.message}</p>
          <button className="secondary" onClick={() => void locations.refetch()}>
            {t("availability.retry")}
          </button>
        </div>
      )}
      {locations.isSuccess && markets.length === 0 && (
        <p className="muted">{t("availability.noMarkets")}</p>
      )}
      {market && Match.value(query).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("availability.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) => (
          <div className="alert" role="alert">
            <p>{error.message}</p>
            <button className="secondary" onClick={() => void query.refetch()}>
              {t("availability.retry")}
            </button>
          </div>
        )),
        Match.when({ status: "success" }, () =>
          groups.length === 0 ? (
            <p className="muted">{t("availability.empty")}</p>
          ) : (
            <>
              <div className="availability-summary" role="status">
                <span>
                  {switchedOff.length === 0
                    ? t("availability.allOn")
                    : t("availability.offCount", { count: switchedOff.length })}
                </span>
                {switchedOff.length > 0 && (
                  <button
                    className="secondary"
                    onClick={() => {
                      for (const item of switchedOff)
                        mutation.mutate({
                          locationId: market,
                          itemId: item.id,
                          available: true,
                        });
                    }}
                  >
                    {t("availability.allBackOn")}
                  </button>
                )}
              </div>
              {groups.map(({ category, items: entries }) => (
                <section key={category} className="settings-card availability-group">
                  <h2>{category}</h2>
                  <ul>
                    {entries.map((item) => (
                      <li
                        key={item.id}
                        className={item.available ? undefined : "switched-off"}
                      >
                        {item.image ? (
                          <img src={item.image} alt="" loading="lazy" />
                        ) : (
                          <span className="availability-thumb" aria-hidden="true" />
                        )}
                        <span className="availability-name">
                          {item.name}
                          {item.dietary && item.dietary.length > 0 && (
                            <span
                              className="dietary-badges"
                              aria-label={item.dietary
                                .map((tag) => t(`dietary.${tag}`))
                                .join(", ")}
                            >
                              {item.dietary.map((tag) => (
                                <abbr key={tag} title={t(`dietary.${tag}`)}>
                                  {DIETARY_BADGES[tag]}
                                </abbr>
                              ))}
                            </span>
                          )}
                          {!item.available && (
                            <span className="pill">{t("availability.off")}</span>
                          )}
                        </span>
                        <button
                          role="switch"
                          className="availability-switch"
                          aria-checked={item.available}
                          aria-label={t("availability.sell", { name: item.name })}
                          onClick={() =>
                            mutation.mutate({
                              locationId: market,
                              itemId: item.id,
                              available: !item.available,
                            })
                          }
                        >
                          <span />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </>
          ),
        ),
        Match.exhaustive,
      )}
    </div>
  );
}
